import type { NavbarAttributes } from '../../types/cms'
import type {
  StrapiListResponse,
  StrapiSingleResponse,
} from '../../types/strapi'
import type { TechnologyPageAttributes } from '../../types/siteData'
import { HeaderGroup } from 'ui-kit'
import { HEALTHXCHANGE_EPHARMACY_DEVICES_CATEGORY_URL } from 'app-core'
import { Box, Button, ListItemText } from '@mui/material'
import { useLocation } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import SearchItem from './SearchItem'
import UiKitLink from './UiKitLink'

type HeaderProps = {
  navbar?: StrapiSingleResponse<NavbarAttributes> | null
  products?: StrapiListResponse<TechnologyPageAttributes> | null
}

const Header = ({ navbar, products }: HeaderProps) => {
  const location = useLocation()
  const asPath = `${location.pathname}${location.searchStr}${location.hash ? `#${location.hash}` : ''}`
  const [searchValue, setSearchValue] = useState('')

  const navbarData = useMemo(() => {
    const navbarItems = navbar?.data?.attributes?.items?.data ?? []
    if (!navbarItems.length) return []

    return navbarItems.flatMap(item => {
      const text = item.attributes?.title
      const href = item.attributes?.url
      if (!text || !href) return []

      const options = (item.attributes?.children?.data ?? [])
        .map(child => {
          const optionText = child.attributes?.title
          const optionHref = child.attributes?.url
          if (!optionText || !optionHref) return null
          return { text: optionText, href: optionHref }
        })
        .filter((option): option is { text: string; href: string } =>
          Boolean(option),
        )

      return [
        {
          text,
          href,
          ...(options.length ? { options } : {}),
        },
      ]
    })
  }, [navbar])

  const filteredData = useMemo(() => {
    if (!searchValue) return []

    const productData = products?.data ?? []
    const query = searchValue.toLowerCase()
    return productData.filter(({ attributes }) =>
      (attributes?.technologyName ?? '').toLowerCase().includes(query),
    )
  }, [products, searchValue])

  return (
    <HeaderGroup
      globalNavProps={{
        activeLink: 'devices',
        linkComponent: UiKitLink,
      }}
      localNavProps={{
        onSearch: setSearchValue,
        searchOptions:
          filteredData.length > 0
            ? filteredData.map(item => (
                <SearchItem
                  item={item}
                  key={item.attributes?.slug ?? item.id}
                />
              ))
            : [
                <Box
                  key="no option"
                  sx={{
                    backgroundColor: '#FFF',
                    color: '#6B6C74',
                    padding: '10px',
                  }}
                >
                  <ListItemText primary="No results found" />
                </Box>,
              ],
        buttons: (
          <Box
            sx={{
              px: { xs: 3, md: 0 },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Button
              variant="contained"
              color="white"
              size="medium"
              fullWidth
              href={HEALTHXCHANGE_EPHARMACY_DEVICES_CATEGORY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Shop Devices
            </Button>
          </Box>
        ),
        searchPlaceholder: 'Search',
        showMyOrderLink: false,
        showOrderHistoryLink: false,
        showIrelandPrompt: false,
        myOrderHref: '',
        linkComponent: UiKitLink,
        currentUrl: asPath,
      }}
      localLinksProps={{
        linkData: navbarData,
        linkComponent: UiKitLink,
      }}
    />
  )
}

export default Header
